import {
  Mutation,
  MutationUpsertUserProfileArgs,
  Query,
} from '@/lib/generated/graphql-types'
import {
  GET_USER_PROFILE,
  UPSERT_USER_PROFILE,
} from '@/modules/user/profile/queries'
import { useQuery, useMutation } from '@apollo/client'
import type { InternalRefetchQueriesInclude } from '@apollo/client/core/types'
import { pick } from 'lodash'
import { useCallback } from 'react'
import cookies from 'js-cookie'
import { ONBOARDING_TYPE_COOKIE_NAME } from '@/modules/onboarding/constants'

export function useUpsertUserProfile(props: {
  onCompleted?: (data: Pick<Mutation, 'upsertUserProfile'>) => void
  isAuthenticated: boolean
}) {
  const { onCompleted, isAuthenticated } = props ?? {}
  const { data } = useQuery<Pick<Query, 'getUserProfile'>>(GET_USER_PROFILE, {
    skip: !isAuthenticated,
  })
  const [_upsertUserProfile, { loading }] = useMutation<
    Pick<Mutation, 'upsertUserProfile'>
  >(UPSERT_USER_PROFILE, {
    onCompleted,
  })
  const userProfile = data?.getUserProfile
  const upsertUserProfile = useCallback(
    async (
      variables: MutationUpsertUserProfileArgs['input'],
      refetchQueries?: InternalRefetchQueriesInclude,
    ) => {
      if (!userProfile) {
        return
      }
      const upsertVariables = {
        variables: {
          input: {
            ...pick(userProfile, [
              'lastName',
              'firstName',
              'homeAddress',
              'countryCode',
              'onboardingType',
              'phone',
              'phoneCountryCode',
              'theme',
              'title',
            ]),
            ...variables,
          },
        },
      }
      if (variables.onboardingType) {
        cookies.set(ONBOARDING_TYPE_COOKIE_NAME, variables.onboardingType)
      }

      await _upsertUserProfile({
        ...upsertVariables,
        refetchQueries: refetchQueries,
      })
    },
    [_upsertUserProfile, userProfile],
  )
  return {
    data,
    upsertUserProfile,
    isLoading: loading,
  }
}
